import React from 'react';
import { useAuth } from '../../Components/AuthContext';
import hasPermission from '../../Hooks/hasPermission';

/**
 * Interruptor de la vista de Developer.
 *
 * ## Por qué existe
 *
 * Tener el permiso y querer ver la sección todo el tiempo son cosas distintas. El
 * bloque de Developer es visualmente muy distinto al resto —negro, verde, animado— y
 * eso es a propósito, pero también significa que **compite por la atención** cuando se
 * está trabajando en la operación normal, o cuando se comparte pantalla con alguien.
 * El interruptor permite guardarlo sin renunciar al permiso.
 *
 * ## Dos condiciones, no una
 *
 * `enabled` = tiene el permiso **y** lo dejó encendido. Se separan porque:
 * - sin permiso, el interruptor no debe existir siquiera (no tiene nada que activar), y
 * - con permiso, la preferencia es del usuario y se recuerda entre sesiones.
 *
 * 🔴 Nada de esto es seguridad. Vive en el navegador y se puede cambiar desde la
 * consola. Lo único que protege los datos es `authorize([DEV_ACCESS])` en el backend,
 * endpoint por endpoint.
 */

const STORAGE_KEY = 'dylo-v2-developer-mode';

interface DeveloperModeValue {
  /** El usuario tiene `DEV_ACCESS`. Si es falso, el interruptor no se muestra. */
  available: boolean;
  /** Tiene el permiso **y** la vista está encendida. */
  enabled: boolean;
  toggle: () => void;
  setEnabled: (value: boolean) => void;
}

const DeveloperModeContext = React.createContext<DeveloperModeValue>({
  available: false,
  enabled: false,
  toggle: () => {},
  setEnabled: () => {},
});

export const DeveloperModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const available = hasPermission(user?.permissionsActives, 'DEV_ACCESS');

  /**
   * Arranca **encendido** salvo que se haya apagado a propósito.
   *
   * Es lo contrario de lo habitual para una preferencia opcional, y es deliberado:
   * quien tiene este permiso lo pidió para usarlo. Que la primera vez aparezca apagado
   * obligaría a ir a buscar el interruptor sin saber que existe.
   */
  const [stored, setStored] = React.useState<boolean>(() => {
    try {
      return window.localStorage.getItem(STORAGE_KEY) !== 'off';
    } catch {
      // Modo privado o almacenamiento bloqueado: se asume encendido y no se persiste.
      return true;
    }
  });

  const setEnabled = React.useCallback((value: boolean) => {
    setStored(value);
    try {
      window.localStorage.setItem(STORAGE_KEY, value ? 'on' : 'off');
    } catch {
      /* la preferencia no se recuerda, pero la sesión funciona */
    }
  }, []);

  const toggle = React.useCallback(() => setEnabled(!stored), [setEnabled, stored]);

  const value = React.useMemo(
    () => ({ available, enabled: available && stored, toggle, setEnabled }),
    [available, stored, toggle, setEnabled]
  );

  return (
    <DeveloperModeContext.Provider value={value}>{children}</DeveloperModeContext.Provider>
  );
};

export const useDeveloperMode = () => React.useContext(DeveloperModeContext);
